import { Platform } from 'react-native';

const fromEnv = process.env.EXPO_PUBLIC_API_BASE;
const isDev = typeof __DEV__ !== 'undefined' ? __DEV__ : process.env.NODE_ENV !== 'production';
const PRODUCTION_API_BASE = 'https://vrmsgr.pro';

/**
 * Сайт, от которого строятся публичные ссылки на профиль и каналы (стори 18.3).
 *
 * <p>Раньше здесь стоял жёсткий прод-адрес: на стенде «Скопировать ссылку на профиль» отдавала
 * ссылку на боевой сайт с ником, которого там может не быть, — то есть проверить свою же
 * ссылку было нечем.
 *
 * <p>Порядок такой: явная настройка сборки → в вебе адрес самой страницы (стенд проверяет сам
 * себя) → прод. В нативной сборке `window` нет, поэтому без настройки остаётся прод — и это
 * правильно: телефон ходит на прод-API и должен раздавать прод-ссылки.
 */
function resolvePublicWebOrigin(): string {
  const fromEnvOrigin = process.env.EXPO_PUBLIC_WEB_ORIGIN;
  if (fromEnvOrigin !== undefined && fromEnvOrigin.trim() !== '') {
    return fromEnvOrigin.trim().replace(/\/$/, '');
  }
  if (typeof window !== 'undefined' && window.location?.origin) {
    return window.location.origin.replace(/\/$/, '');
  }
  return PRODUCTION_API_BASE;
}

/** Canonical site for public profile/channel URLs (independent of API host in dev). */
export const PUBLIC_WEB_ORIGIN = resolvePublicWebOrigin();

/**
 * В __DEV__ нативные сборки по умолчанию ходят на прод (как release / store).
 * Локальный Spring на ПК: `EXPO_PUBLIC_DEV_LOCAL_API=true` (эмулятор 10.0.2.2, iOS симулятор 127.0.0.1:18080).
 */
const preferLocalApiInDev =
  process.env.EXPO_PUBLIC_DEV_LOCAL_API === '1' ||
  process.env.EXPO_PUBLIC_DEV_LOCAL_API === 'true';

function isPrivateIpv4(host: string): boolean {
  const m = host.match(/^(\d+)\.(\d+)\.(\d+)\.(\d+)$/);
  if (!m) return false;
  const a = Number(m[1]);
  const b = Number(m[2]);
  if (a === 10) return true;
  if (a === 192 && b === 168) return true;
  if (a === 172 && b >= 16 && b <= 31) return true;
  return false;
}

function defaultApiBase(): string {
  if (isDev && preferLocalApiInDev) {
    if (Platform.OS === 'android') {
      return 'http://10.0.2.2:18080';
    }
    if (Platform.OS === 'ios') {
      return 'http://127.0.0.1:18080';
    }
  }
  if (typeof window !== 'undefined' && window.location?.hostname) {
    const h = window.location.hostname;
    // Expo / Metro web (localhost): /api на dev-сервере отдаёт index.html — бьём напрямую в Spring.
    if (h === 'localhost' || h === '127.0.0.1') {
      return 'http://127.0.0.1:18080';
    }
    // Dev по LAN IP (например, http://192.168.x.x:8081): backend обычно на том же хосте, но на 18080.
    if (isPrivateIpv4(h)) {
      return `http://${h}:18080`;
    }
    // Прод: один домен с nginx → относительные URL на тот же origin.
    return '';
  }
  if (Platform.OS !== 'web' && isDev) {
    return PRODUCTION_API_BASE;
  }
  return isDev ? 'http://127.0.0.1:18080' : PRODUCTION_API_BASE;
}

/**
 * EXPO_PUBLIC_API_BASE: явный URL API (перекрывает всё ниже).
 * EXPO_PUBLIC_DEV_LOCAL_API=true: в __DEV__ на iOS/Android — локальный Spring (см. defaultApiBase).
 * Иначе в __DEV__ нативные сборки по умолчанию идут на прод (https://vrmsgr.pro).
 * Web: localhost / LAN → :18080; боевой домен → '' (тот же origin + nginx /api).
 */
function trimTrailingSlash(s: string): string {
  return s.replace(/\/$/, '');
}

/**
 * На vrmsgr.pro UI и `/api` обслуживает один nginx. Если в web-бандл зашит
 * `EXPO_PUBLIC_API_BASE=https://vrmsgr.pro`, а вкладка открыта с `www.vrmsgr.pro`,
 * fetch уходит на другой host → отдельный localStorage и лишний CORS; часть
 * запросов (в т.ч. с Bearer) ведёт себя иначе, чем локально на 127.0.0.1:18080.
 * Same-origin (`''`) устраняет это независимо от того, что заинлайнил webpack.
 */
function apiBaseForProductionWeb(): string | null {
  if (typeof window === 'undefined') return null;
  const host = window.location?.hostname;
  if (!host) return null;
  const h = host.toLowerCase();
  if (h === 'vrmsgr.pro' || h === 'www.vrmsgr.pro') {
    return '';
  }
  return null;
}

/**
 * Адрес API, продиктованный десктопной оболочкой (папка `desktop/`).
 *
 * <p>Там этот же бандл открыт со своего http-сервера на 127.0.0.1, а `/api` и `/ws` он
 * проксирует на боевой сервер сам. Оболочке нужен пустой (same-origin) базовый адрес: с
 * абсолютным запросы пошли бы мимо прокси прямо на vrmsgr.pro и были бы отбиты по CORS —
 * страница на 127.0.0.1 для сервера чужая, а в WebSocket вайлдкард запрещён явно.
 *
 * <p>Значение приходит из preload через contextBridge — тем же способом, что и
 * `window.__VRM_ICE_SERVERS__` у веб-сборки: настройка окружения, не решение приложения.
 */
function apiBaseFromDesktopShell(): string | null {
  if (typeof window === 'undefined') return null;
  const shell = (window as unknown as { vrmDesktop?: { apiBase?: unknown } }).vrmDesktop;
  return shell && typeof shell.apiBase === 'string' ? shell.apiBase : null;
}

const resolvedBase = (() => {
  const desktop = apiBaseFromDesktopShell();
  if (desktop !== null) {
    return desktop;
  }
  const prodWeb = apiBaseForProductionWeb();
  if (prodWeb !== null) {
    return prodWeb;
  }
  return fromEnv !== undefined && fromEnv !== '' ? fromEnv : defaultApiBase();
})();

export const API_BASE = trimTrailingSlash(resolvedBase);

if (!API_BASE && Platform.OS !== 'web') {
  throw new Error(
    'EXPO_PUBLIC_API_BASE is required for native release builds. Example: https://vrmsgr.pro',
  );
}

/** WebRTC may mutate passed `RTCIceServer` objects; never return shared refs from `window.__VRM_ICE_SERVERS__`. */
function cloneIceServers(servers: RTCIceServer[]): RTCIceServer[] {
  return servers.map((s) => {
    const urls = s.urls;
    const out: RTCIceServer = {
      urls: Array.isArray(urls) ? [...urls] : urls,
    };
    if (typeof s.username === 'string') {
      out.username = s.username;
    }
    if (typeof s.credential === 'string') {
      out.credential = s.credential;
    }
    return out;
  });
}

/**
 * iOS WebRTC: следует ли принудительно включать `iceTransportPolicy: relay` при наличии TURN
 * (см. webrtcPlatform).
 *
 * По умолчанию — НЕТ (`all`): iOS, как и web, использует host/srflx/relay вместе, а ICE сам
 * выбирает лучшую пару (прямую, если сеть позволяет; TURN как fallback). Прежний дефолт
 * relay-only ломал ВСЕ звонки с участием iOS (iOS↔iOS, web↔iOS), когда coturn-релей недоступен
 * (не открыт UDP-диапазон, неверный external IP и т.п.), тогда как web продолжал работать через
 * прямые кандидаты — ровно тот симптом, что мы наблюдали.
 *
 * Чтобы вернуть строгий relay-only (например, для приватности/симметричных NAT с рабочим TURN),
 * задайте `EXPO_PUBLIC_IOS_ICE_TRANSPORT=relay`.
 */
export function iosUseRelayOnlyWhenTurnPresent(): boolean {
  const v = process.env.EXPO_PUBLIC_IOS_ICE_TRANSPORT?.trim().toLowerCase();
  return v === 'relay';
}

export function getIceServers(): RTCIceServer[] {
  const runtimeAny = globalThis as unknown as { __VRM_ICE_SERVERS__?: unknown };
  const runtimeIce = runtimeAny.__VRM_ICE_SERVERS__;
  if (Array.isArray(runtimeIce)) {
    const servers = runtimeIce.filter(
      (x): x is RTCIceServer => !!x && typeof x === 'object' && 'urls' in (x as Record<string, unknown>),
    );
    if (servers.length > 0) {
      return cloneIceServers(servers);
    }
  }
  const raw = process.env.EXPO_PUBLIC_ICE_SERVERS;
  if (!raw) {
    return cloneIceServers([{ urls: 'stun:stun.l.google.com:19302' }]);
  }
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (!Array.isArray(parsed)) {
      return cloneIceServers([{ urls: 'stun:stun.l.google.com:19302' }]);
    }
    const servers = parsed.filter(
      (x): x is RTCIceServer => !!x && typeof x === 'object' && 'urls' in (x as Record<string, unknown>),
    );
    return servers.length > 0
      ? cloneIceServers(servers)
      : cloneIceServers([{ urls: 'stun:stun.l.google.com:19302' }]);
  } catch {
    return cloneIceServers([{ urls: 'stun:stun.l.google.com:19302' }]);
  }
}

export function wsUrlCandidates(wsTicket?: string): string[] {
  let httpBase = API_BASE;
  if (!httpBase && typeof window !== 'undefined' && window.location) {
    httpBase = `${window.location.protocol}//${window.location.host}`;
  }
  if (!httpBase) {
    httpBase = 'http://127.0.0.1:18080';
  }
  const wsBase = httpBase.replace(/^http/i, (m: string) =>
    m.toLowerCase() === 'https' ? 'wss' : 'ws',
  );
  const out: string[] = [];
  const seen = new Set<string>();
  const push = (url: string) => {
    if (!seen.has(url)) {
      seen.add(url);
      out.push(url);
    }
  };

  const trimEnd = (s: string) => s.replace(/\/+$/, '');
  const baseNoSlash = trimEnd(wsBase);
  const ticketQuery = wsTicket ? `?wsTicket=${encodeURIComponent(wsTicket)}` : '';
  push(`${baseNoSlash}/ws${ticketQuery}`);
  push(`${baseNoSlash}/api/ws${ticketQuery}`);

  try {
    const u = new URL(httpBase);
    const wsOrigin = `${u.protocol === 'https:' ? 'wss:' : 'ws:'}//${u.host}`;
    const basePath = u.pathname.replace(/\/+$/, '');
    if (basePath) {
      push(`${wsOrigin}${basePath}/ws${ticketQuery}`);
    }
  } catch {
    // keep defaults above
  }
  return out;
}

export function wsAuthHeaders(accessToken: string): Record<string, string> {
  return {
    Authorization: `Bearer ${accessToken}`,
  };
}
